/** Constants for the search feature. */

// ─── Filter selection limits ────────────────────────────────────────────────

/** Maximum number of items that can be selected in the String Group filter */
export const MAX_SELECTION_COUNT = 100;

/**
 * Maximum namespaces selectable in the Namespace filter. Held below
 * MAX_SELECTION_COUNT so a shared search URL stays short, and matched by the
 * server's own request cap (SearchPermissionScope.MAX_REQUESTED_NAMESPACES).
 */
export const MAX_NAMESPACE_SELECTION_COUNT = 50;

// ─── Fetch-all pagination limits ────────────────────────────────────────────

/** Maximum pages to fetch when paginating to guard against infinite loops */
export const MAX_PAGES = 50;

/** Maximum pageSize allowed by the Smithy PaginationParameters (range max: 100) */
export const MAX_PAGE_SIZE = 100;

/**
 * Upper bound on the number of items a fetch-all can return before pagination
 * is capped. Used in the user-facing truncation hints below.
 */
export const MAX_FETCHED_ITEMS = MAX_PAGES * MAX_PAGE_SIZE;

/**
 * Hints surfaced on a filter dropdown when its fetch-all stopped at the page cap. Without these
 * the cap is silent in production, since the console.warn is dev-only. Each interpolates
 * `{count}` with {@link MAX_FETCHED_ITEMS}, formatted by ICU for the active locale.
 */
export const TRUNCATION_HINT_IDS = {
  NAMESPACES: 'search-truncation-namespaces',
  STRING_GROUPS: 'search-truncation-string-groups',
  CX_FAMILIES: 'search-truncation-cx-families',
} as const;

// ─── Search submission validation ───────────────────────────────────────────

/**
 * Resource id for the reason shown when source-string search is blocked because no
 * criterion has been supplied. Shared by its two consumers so they can never
 * drift:
 *   - the search panel's footer message (the pre-submit gate) — derived in
 *     `SearchPage` and rendered by `SearchPanelFooter`, which the query input
 *     points its `aria-describedby` at
 *   - `SearchResultsTable`'s criteria gate, the backstop for paths that re-fire
 *     an existing search (sort change, pagination, Retry)
 *
 * The backend enforces the same rule and answers an unconstrained search with a
 * 400 ValidationException, so this wording explains up front what the API would
 * otherwise refuse; a blank `queryText` counts as omitted on both sides. An empty
 * `queryText` is still supported once at least one filter is set. See
 * `hasMinimumSearchCriteria` in `searchFilterUtils.ts`.
 */
export const MIN_SEARCH_CRITERIA_MESSAGE_ID = 'search-gate-min-criteria';

/**
 * Shortest free-text query the backend can resolve: the OpenSearch analyzers
 * behind both search paths are built around a three-character minimum, so shorter
 * terms match inconsistently rather than narrowly. A backend constraint, not a
 * product preference.
 */
export const MIN_QUERY_LENGTH = 3;

/** Shared by `SearchPage` and `SearchResultsTable` so their gates cannot drift. */
export const MIN_QUERY_LENGTH_MESSAGE_ID = 'search-gate-min-query-length';

/** Translation mode's required-filter reason; shared so its gates cannot drift. */
export const TRANSLATION_LOCALE_REQUIRED_MESSAGE_ID =
  'search-gate-translation-locale-required';

// ─── Mandatory namespace filter (large-access tenants) ──────────────────────

/**
 * Above this many accessible namespaces, a search must be scoped to at least one
 * namespace — an unscoped query over hundreds of namespaces is the pathological
 * OpenSearch case. Kept in sync with the server guardrail
 * (SearchPermissionScope.MANDATORY_NAMESPACE_FILTER_THRESHOLD).
 */
export const MANDATORY_NAMESPACE_FILTER_THRESHOLD = 50;

/** Shown when the gate blocks an unscoped search for a large-access tenant. */
export const MANDATORY_NAMESPACE_FILTER_MESSAGE_ID =
  'search-gate-mandatory-namespace';

/** Shown while the accessible-namespace count is still loading and manual submit is gated. */
export const NAMESPACE_LOADING_MESSAGE_ID = 'search-namespaces-loading';

// ─── Stable references ──────────────────────────────────────────────────────
/**
 * Stable empty array reference to avoid creating a new [] on every render,
 * which would cause spurious useEffect re-fires when used as a dependency.
 */
export const EMPTY_ARRAY: string[] = [];
